import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { navItems } from '@/mocks/navigation';
import { categories } from '@/mocks/categories';

const navIcons: Record<string, string> = {
  'Home': 'ri-home-4-line',
  'Shopping Guide': 'ri-shopping-bag-3-line',
  'Guide': 'ri-book-open-line',
  'Review': 'ri-star-line',
  'Templates': 'ri-file-copy-line',
  'Security': 'ri-shield-check-line',
  'Comparison': 'ri-scales-line',
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      setMobileOpen(false);
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-excel-2-line text-lg"></i>
            </div>
            <span className="font-heading text-lg text-foreground-900 whitespace-nowrap">
              VigorBuy Guide
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              item.href.startsWith('/') && !item.href.startsWith('/#') ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="px-4 py-2 text-sm text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    if (item.href.startsWith('#')) {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }
                  }}
                  className="px-4 py-2 text-sm text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
                >
                  {item.label}
                </a>
              )
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <div className="mb-3 pb-3 border-b border-background-200/50">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-2">
                Navigation
              </span>
            </div>
            {navItems.map((item) => (
              item.href.startsWith('/') && !item.href.startsWith('/#') ? (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className={`${navIcons[item.label] || 'ri-compass-3-line'} text-sm`}></i>
                  </div>
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    if (item.href.startsWith('#')) {
                      e.preventDefault();
                      handleNavClick(item.href);
                    } else {
                      setMobileOpen(false);
                    }
                  }}
                  className="px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                    <i className={`${navIcons[item.label] || 'ri-compass-3-line'} text-sm`}></i>
                  </div>
                  {item.label}
                </a>
              )
            ))}

            <div className="mt-3 pt-3 border-t border-background-200/50">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-2">
                Shop Categories
              </span>
            </div>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 flex items-center gap-3"
              >
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground-800 truncate">{cat.name}</div>
                  <div className="text-xs text-foreground-500 truncate">{cat.description}</div>
                </div>
                <i className="ri-arrow-right-up-line text-xs text-foreground-400 flex-shrink-0"></i>
              </a>
            ))}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-5 py-3 text-sm font-medium text-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}